<script setup lang="ts">
/**
 * App Header
 * Adapts based on auth state:
 *   - Public mode: Logo + nav links
 *   - Authenticated: Logo + BCU widget + AI model selector + user menu
 * AI Model selector only shown when on a project page (currentProject is set).
 */
import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useProjectsStore } from '@/stores/projects'
import NavLinks from './NavLinks.vue'
import UserMenu from './UserMenu.vue'
import BcuBalanceWidget from './BcuBalanceWidget.vue'
import AiModelSelector from './AiModelSelector.vue'
import LanguageSwitcher from './LanguageSwitcher.vue'

defineProps<{
  publicMode?: boolean
}>()

const authStore = useAuthStore()
const projectsStore = useProjectsStore()

const hasActiveProject = computed(() => !!projectsStore.currentProject)
</script>

<template>
  <header
    :class="[
      'sticky top-0 flex items-center justify-between px-6 h-[60px] border-b border-border z-[var(--z-sticky)] max-md:px-4',
      publicMode ? 'bg-surface-secondary' : 'bg-surface',
    ]"
  >
    <div class="flex items-center gap-3">
      <RouterLink
        :to="authStore.isAuthenticated ? '/projects' : '/'"
        class="flex items-center gap-2.5 no-underline text-foreground"
      >
        <img src="/images/logos/logo-no-text-favicon.png" alt="Code Board" class="w-8 h-8 object-contain">
        <span class="text-lg font-bold bg-gradient-to-br from-brand to-brand-light bg-clip-text text-transparent max-md:hidden">
          Code Board
        </span>
      </RouterLink>
    </div>

    <!-- Public mode: nav links -->
    <NavLinks v-if="publicMode" />

    <!-- Authenticated mode: center slot -->
    <div v-if="!publicMode && authStore.isAuthenticated" class="flex items-center flex-1 justify-center max-md:hidden">
      <slot name="center" />
    </div>

    <div class="flex items-center gap-4">
      <!-- Authenticated widgets -->
      <template v-if="authStore.isAuthenticated && !publicMode">
        <AiModelSelector v-if="hasActiveProject" />
        <BcuBalanceWidget />
      </template>

      <LanguageSwitcher />

      <UserMenu v-if="authStore.isAuthenticated" />
    </div>
  </header>
</template>
